import { Plus, Trash2 } from "lucide-react";
import type { ReceiptDraft } from "../../types/receipt";
import type { ReceiptState } from "../../lib/receipt-draft";
import { money } from "../../lib/finance";
import ItemFields from "./ReceiptItemFields";

/** A scanned receipt's items: which to track prices for, corrections, and anything the scan missed. */
export default function ReceiptReviewItems({ state, busy, editState, editDraft, allowTracking = true }: {
  state: ReceiptState;
  busy: boolean;
  editState: (patch: Partial<ReceiptState>) => void;
  editDraft: (patch: Partial<ReceiptDraft>) => void;
  allowTracking?: boolean;
}) {
  function toggleTrackingItem(id: string) {
    const current = state.trackingItemIds || [];
    editState({
      trackingItemIds: current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id],
    });
  }
  return (
    <>
      {allowTracking && <div className="receipt-actions receipt-review-toolbar">
        <span>
          {(state.trackingItemIds || []).length} selected for tracking
        </span>
        <button
          type="button"
          disabled={
            busy || state.pending || state.draft.items.length === 0
          }
          onClick={() =>
            editState({
              trackingItemIds:
                (state.trackingItemIds || []).length ===
                state.draft.items.length
                  ? []
                  : state.draft.items.map((item) => item.id),
            })
          }
        >
          {(state.trackingItemIds || []).length ===
          state.draft.items.length
            ? "Clear tracking"
            : "Select all tracking"}
        </button>
      </div>}
      <fieldset disabled={busy || state.pending}>
        {state.draft.items.map((item, index) => {
          const wantsTracking = (state.trackingItemIds || []).includes(
            item.id,
          );
          return (
            <details
              className={`receipt-item receipt-item-compact ${
                wantsTracking ? "receipt-item-selected" : ""
              }`}
              key={item.id}
            >
              <summary>
                <span>
                  {index + 1}. {item.name || item.original}
                  <small>
                    {[
                      item.quantity ? `${item.quantity} bought` : "",
                      item.pack,
                      allowTracking ? wantsTracking ? "Tracking" : "Not tracking" : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </span>
                <span className="receipt-review-actions">
                  <strong>
                    {item.total
                      ? money(Number(item.total), state.draft.currency)
                      : "Check price"}
                  </strong>
                  {allowTracking && <button
                    type="button"
                    className="track-toggle receipt-review-track"
                    aria-pressed={wantsTracking}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleTrackingItem(item.id);
                    }}
                  >
                    {wantsTracking ? "Tracking" : "Not tracking"}
                  </button>}
                </span>
              </summary>
              <ItemFields
                item={item}
                tracking
                change={(patch) =>
                  editDraft({
                    items: state.draft.items.map((i) =>
                      i.id === item.id ? { ...i, ...patch } : i,
                    ),
                  })
                }
              />
              <button
                className="text-button"
                onClick={() =>
                  editState({
                    draft: {
                      ...state.draft,
                      items: state.draft.items.filter(
                        (i) => i.id !== item.id,
                      ),
                    },
                    trackingItemIds: (
                      state.trackingItemIds || []
                    ).filter((id) => id !== item.id),
                  })
                }
              >
                <Trash2 size={15} /> Remove item
              </button>
            </details>
          );
        })}
        <button
          className="text-button"
          onClick={() =>
            editDraft({
              items: [
                ...state.draft.items,
                {
                  id: crypto.randomUUID(),
                  original: "New item",
                  name: "",
                  brand: "",
                  group: "",
                  quantity: "",
                  total: "",
                  pack: "",
                  measure: "",
                  unit: "",
                },
              ],
            })
          }
          disabled={state.draft.items.length >= 100}
        >
          <Plus size={16} /> Add missing item
        </button>
        <label>
          Discounts, tax & other adjustments
          <textarea
            rows={3}
            maxLength={1000}
            value={state.draft.adjustments}
            onChange={(e) => editDraft({ adjustments: e.target.value })}
          />
        </label>
      </fieldset>
    </>
  );
}
